'use client';

import { ArrowLeftIcon } from '@app/app/_icons/ArrowLeftIcon';
import { MenuIcon } from '@app/app/_icons/MenuIcon';
import { motion } from 'framer-motion';
import { ReactNode, useMemo } from 'react';
import { twJoin } from 'tailwind-merge';
import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Heading } from '@app/app/_components/Heading/Heading';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { TextButton } from '@app/app/_components/TextButton/TextButton';

interface Props {
    title: string;
    subtitle: ReactNode;
    currentStepIndex: number;
    totalSteps: number;
    onBack?: () => void;
    onMenuClick?: () => void;
    className?: string;
}

export function MobileWizardHeader({
    title,
    subtitle,
    currentStepIndex,
    totalSteps,
    onBack,
    onMenuClick,
    className = '',
}: Props) {
    const progressPercentage = useMemo(() => {
        return ((currentStepIndex + 1) / totalSteps) * 100;
    }, [currentStepIndex, totalSteps]);

    return (
        <div className={twJoin('left-0 right-0 top-0 z-50 bg-white md:hidden', className)}>
            <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
                <div className="flex items-center gap-3">
                    {onBack && (
                        <TextButton onClick={onBack} className="p-2" preventDefault>
                            <ArrowLeftIcon className="size-5" />
                        </TextButton>
                    )}
                    <div>
                        <Heading level="h1" className="font-semibold text-lg">
                            {title}
                        </Heading>
                        {subtitle && (
                            <Paragraph size="small" className="text-neutral-600">
                                {subtitle}
                            </Paragraph>
                        )}
                    </div>
                </div>

                {onMenuClick && (
                    <GhostButton onClick={onMenuClick} className="p-2">
                        <MenuIcon className="size-6" />
                    </GhostButton>
                )}
            </div>

            <div className="h-1 bg-neutral-200">
                <motion.div
                    className="h-full bg-primary-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${progressPercentage}%` }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                />
            </div>
        </div>
    );
}
